'use client'

import { useCallback, useState } from 'react'

import {
  useAnswerQuizQuestion,
  useFinishQuizAttempt,
  useQuizAttempt
} from '../hooks/useQuizAttempt'
import { firstUnansweredIndex } from '../lib/quiz-ui'
import type { QuizAttempt } from '../types/quiz'

import { Button } from '@/shared/ui/Button'

import { QuizAnswerOption } from './QuizAnswerOption'
import { QuizAttemptResult } from './QuizAttemptResult'
import { QuizCountdown } from './QuizCountdown'
import { QuizProgressPips } from './QuizProgressPips'

interface QuizAttemptRunnerProps {
  initialAttempt: QuizAttempt
}

export function QuizAttemptRunner({ initialAttempt }: QuizAttemptRunnerProps) {
  const { data: attempt, refetch } = useQuizAttempt(initialAttempt.id)
  const current = attempt ?? initialAttempt

  const answer = useAnswerQuizQuestion(current.id)
  const finish = useFinishQuizAttempt(current.id)

  // Retomada abre onde o aluno parou. Só no mount: depois disso quem manda na
  // navegação é ele (pips e "Voltar"), e recalcular a cada resposta arrastaria
  // a tela para longe da pergunta em que está.
  const [index, setIndex] = useState(() =>
    firstUnansweredIndex(initialAttempt.quiz_attempt_questions ?? [])
  )

  // O relógio local só pede revalidação: quem encerra a tentativa é o backend,
  // no primeiro request que a tocar.
  const handleExpire = useCallback(() => {
    refetch()
  }, [refetch])

  if (current.status !== 'in_progress') {
    return <QuizAttemptResult attempt={current} />
  }

  const questions = current.quiz_attempt_questions ?? []
  const question = questions[index]
  const isLast = index >= questions.length - 1
  const answeredIndexes = questions.flatMap((item, position) =>
    item.selected_option_id !== null ? [position] : []
  )
  const answeredCount = answeredIndexes.length
  const allAnswered = answeredCount === questions.length

  if (!question) return null

  const handleAnswer = async (optionId: number) => {
    await answer.mutateAsync({
      quizQuestionId: question.quiz_question_id,
      quizQuestionOptionId: optionId
    })

    // Avança sozinho: responder e ter de clicar "Próxima" é um passo a mais em
    // toda pergunta. Na última fica parado, porque o passo seguinte é finalizar.
    if (!isLast) setIndex(value => value + 1)
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <span className="font-hl text-sm font-black uppercase tracking-wide text-jacaranda">
          Pergunta {index + 1} de {questions.length}
        </span>
        <QuizCountdown expiresAt={current.expires_at} onExpire={handleExpire} />
      </div>

      <div
        className="h-2 w-full overflow-hidden rounded-full bg-white/40"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={questions.length}
        aria-valuenow={answeredCount}
        aria-label="Perguntas respondidas"
      >
        <div
          className="h-full rounded-full bg-jacaranda transition-all duration-300"
          style={{ width: `${(answeredCount / questions.length) * 100}%` }}
        />
      </div>

      <QuizProgressPips
        total={questions.length}
        currentIndex={index}
        answeredIndexes={answeredIndexes}
        onSelect={setIndex}
      />

      <div className="rounded-2xl bg-white/70 p-5">
        <p className="font-hl text-lg font-black leading-snug text-american-blue">
          {question.statement}
        </p>

        <div className="mt-5 flex flex-col gap-3">
          {question.quiz_question_options.map((option, optionIndex) => (
            <QuizAnswerOption
              key={option.id}
              index={optionIndex}
              label={option.name}
              selected={question.selected_option_id === option.id}
              disabled={answer.isPending}
              onSelect={() => handleAnswer(option.id)}
            />
          ))}
        </div>
      </div>

      {answer.isError && (
        <p className="text-center text-sm font-semibold text-[#B23B41]">
          Não foi possível registrar a resposta. O tempo pode ter acabado.
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIndex(value => Math.max(value - 1, 0))}
          disabled={index === 0}
        >
          Voltar
        </Button>

        <span className="text-xs font-bold text-[#7F7FAD]">
          {answeredCount} de {questions.length} respondidas
        </span>

        {isLast || allAnswered ? (
          <Button
            variant="success"
            size="sm"
            loading={finish.isPending}
            onClick={() => finish.mutate()}
          >
            Finalizar
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() =>
              setIndex(value => Math.min(value + 1, questions.length - 1))
            }
          >
            Próxima
          </Button>
        )}
      </div>

      {!allAnswered && isLast ? (
        <p className="text-center text-xs font-semibold text-[#7F7FAD]">
          As perguntas que ficarem sem resposta contam como erro.
        </p>
      ) : null}
    </div>
  )
}
